import {
  Component, ChangeDetectorRef, Input, Output,
  OnChanges, OnDestroy, EventEmitter,
} from '@angular/core';
import {
  UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, Validators,
} from '@angular/forms';
import * as _ from 'lodash';

import { TableComponent } from '../../../table/table/table.component';
import { createFormControl } from 'utils/form-control';

import { billingData } from '../billing-data';
import { defaultBillingData } from '../default-billing-data';
import {
  usStateLookup, mxStateLookup, caStateLookup,
} from 'utils/lookups';
import { cardExpirationDateMask, cardNumberMask, cvvMask } from '../../../input-masks';
import {distinctUntilChanged, takeWhile} from 'rxjs/operators';

@Component({
  moduleId: module.id,
  selector: 'manage-billing-form',
  templateUrl: 'manage-billing-form.component.html',
  styleUrls: ['manage-billing-form.component.scss'],
})
export class ManageBillingFormComponent extends TableComponent implements OnChanges, OnDestroy {
  form: UntypedFormGroup;
  segmentData = billingData;
  isActive: boolean = true;

  @Input() data: any = defaultBillingData;

  @Output() formChange = new EventEmitter<any>();

  @Output() formDataChanged = new EventEmitter<any>();

  constructor(
    protected ref: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
  ) {
    super();
    this.createForm();
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnChanges() {
    this.formChange.emit(this.form);
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public clearForm() {
    this.createForm();
    this.ref.markForCheck();
  }

  public hasError(fieldMap: any, form: any) {
    return form.controls[fieldMap].dirty && super.hasError(fieldMap, form);
  }

  public getPlaceholder(item: any) {
    if (item.field_map === 'expDate') { return 'XX/XX'; }
    if (item.field_map === 'creditCard') { return '1234–0000–0000–0000'; }
    return item.title;
  }

  public getMask(item: any): string | undefined | null {
    if (item?.field_map === 'expDate') {
      return '00/00';
    }
    if (item?.field_map === 'creditCard') {
      return '0000-0000-0000-0000';
    }
    if (item?.field_map === 'cvv') {
      return '000';
    }
    return null;
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private createForm() {
    const formControls = _.reduce(this.segmentData, (result: any, item: any) => {
      let value = this.data[item.field_map];
      if (item.lookup && !value) {
        value = _.chain(item.lookup).head().get('option').value();
      }
      const control = new UntypedFormControl(
        value,
        this.getItemValidators(item.validation)
      );
      result[item.field_map] = control;
      return result;
    }, {});

    this.form = new UntypedFormGroup(formControls);
    const state: any = _.find(billingData, ['field_map', 'state']);

    this.form.get('country').valueChanges
      .pipe(distinctUntilChanged(), takeWhile(() => this.isActive))
      .subscribe((selectedCountry: any) => {
        this.changeStates(selectedCountry);
        this.form.get('state').setValue(state.lookup[0].option);
        this.ref.markForCheck();
      });

    if (this.data.country) {
      // update states select list
      this.form.get('country').setValue(this.data.country);
    }

    this.form.valueChanges
      .pipe(distinctUntilChanged(), takeWhile(() => this.isActive))
      .subscribe((form: any) => {
        this.formDataChanged.emit();
      });

    this.ref.markForCheck();
  }

  private getSelectPlaceholder(item: any) {
    if (item.field_map === 'expMonth') {  return 'Month'; }
    if (item.field_map === 'expYear') {   return 'Year'; }
    return 'Select';
  }

  private changeStates(country: string) {
    const state: any = _.find(billingData, ['field_map', 'state']);
    switch (country) {
      case 'US':
        state.lookup = usStateLookup;
        break;
      case 'MX':
        state.lookup = mxStateLookup;
        break;
      case 'CA':
        state.lookup = caStateLookup;
        break;
      default:
        state.lookup = null;
    }
    this.ref.markForCheck();
  }
}
